import React from 'react';
import PropTypes from 'prop-types';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import MenuItem from '@material-ui/core/MenuItem';
import InputAdornment from '@material-ui/core/InputAdornment';
import {
    FirstPage as FirstPageIcon,
    LastPage as LastPageIcon,
    ChevronRight as ChevronRightIcon,
    ChevronLeft as ChevronLeftIcon,
    MoreHoriz as MoreHorizIcon,
    Search as SearchIcon
} from '@material-ui/icons';
import {createUltimatePagination} from 'react-ultimate-pagination';
import {isPlainObject} from 'lodash/lang';
import {calcTableEntriesInfo} from 'utils/common';
import {LoadingContext} from '../../contexts/LoadingContext';


class ThinkscapeTableWrapper extends React.Component {
    static propTypes = {
        TableCore: PropTypes.func.isRequired,
        fetchRecords: PropTypes.func.isRequired,
        setLoading: PropTypes.func,
        searchBoxPlaceholder: PropTypes.string,
        ExtraFilters: PropTypes.func,
        extraFilterProps: PropTypes.object,
        restProps: PropTypes.object.isRequired,
    };

    constructor(props) {
        super(props);
        this.state = {
            recordPages: {
                currentPage: 1,
                totalCount: 0,
                pageCount: 1,
                itemsPerPage: 0,
            },
            page: 1,
            itemsPerPage: this.props.restProps.itemsPerPage ? this.props.restProps.itemsPerPage  : 10,
            filterValue: '',
            sortOpts: {},
            extraFilters: {},
        };
        this.setRecords = this.setRecords.bind(this);
        this.setCurrentRecordPage = this.setCurrentRecordPage.bind(this);
        this.clearRecordsAfterPage = this.clearRecordsAfterPage.bind(this);
        this.handlePageSelect = this.handlePageSelect.bind(this);
        this.handleRecordsPerPageSelect = this.handleRecordsPerPageSelect.bind(this);
        this.handleFilterChange = this.handleFilterChange.bind(this);
        this.handleSortableFieldClick = this.handleSortableFieldClick.bind(this);
        this.handleRefreshTable = this.handleRefreshTable.bind(this);
        this.handleExtraFiltersChange = this.handleExtraFiltersChange.bind(this);
        this.extraFiltersRef = null;
    }

    componentDidMount() {
        if (this.extraFiltersRef && this.extraFiltersRef.defaultValues) {
            this.handleExtraFiltersChange(this.extraFiltersRef.defaultValues);
        } else {
            this.handleRefreshTable(true);
        }
    }

    componentWillUnmount() {
        this.clearRecordsAfterPage(0);
    }

    setRecords({items, currentPage = 1, pageCount, totalCount, itemsPerPage}) {
        const sortedIds = [];
        const newRecordPage = {};
        if ( items && items.length !== 0 ){
        for (let record of items) {
            const id = record.hasOwnProperty('_id') ? record._id.$oid : record.id;
            sortedIds.push(id);
            newRecordPage[id] = record;
        }
        
        newRecordPage['sortedIds'] = sortedIds;
        this.setState(prevState => ({
            recordPages: {
                ...prevState.recordPages,
                [currentPage]: newRecordPage,
                currentPage,
                totalCount,
                pageCount,
                itemsPerPage,
            },
        }));
    }
    }

    setCurrentRecordPage(page) {
        this.setState(prevState => ({
            recordPages: {
                ...prevState.recordPages,
                currentPage: page,
            },
        }));
    }

    clearRecordsAfterPage(page) {
        page = parseInt(page) || 0;
        this.setState(prevState => {
            const pageNumbers = Object.keys(prevState.recordPages).filter(v => {
                const parsed = parseInt(v);
                return !isNaN(parsed) && parsed <= page;
            });
            const recordPages = pageNumbers.reduce((obj, num) => ({
                ...obj,
                [num]: {
                    ...prevState.recordPages[num],
                },
            }), {});
            return {
                recordPages: {
                    ...recordPages,
                    currentPage: page || 1,
                    totalCount: prevState.recordPages.totalCount,
                    pageCount: prevState.recordPages.pageCount,
                    itemsPerPage: prevState.recordPages.itemsPerPage,
                }
            };
        });
    }

    handlePageSelect(page) {
        const {recordPages} = this.state;
        if (page === recordPages.currentPage) {
            return;
        }
        this.setState({
            page,
        }, () => {
            if (recordPages.hasOwnProperty(page)) {
                this.setCurrentRecordPage(page);
            } else {
                this.handleRefreshTable();
            }
        });
    }

    handleRecordsPerPageSelect({target: {value}}) {
        if (value === this.state.itemsPerPage) {
            return;
        }
        this.setState({
            itemsPerPage: value,
        }, () => this.handleRefreshTable(true));
    }

    handleFilterChange(e) {
        const filterValue = e.target.value;
        this.setState({
            filterValue,
        }, () => this.handleRefreshTable(true));
    }

    handleSortableFieldClick(fieldName, sortDir) {
        let sortOpts = {};
        if (sortDir) {
            sortOpts = {
                sortField: fieldName,
                sortDir,
            };
        }
        this.setState({
            sortOpts,
        }, () => this.handleRefreshTable(true));
    }

    handleRefreshTable(hardRefresh = false) {
        const {fetchRecords, setLoading} = this.props;
        const {page: currentPage, itemsPerPage, filterValue, sortOpts, extraFilters} = this.state;
        const page = hardRefresh ? 1 : currentPage;
        if (filterValue && filterValue.length < 3) {
            return;
        }
        if (setLoading) setLoading(true);
        return fetchRecords({
            q: filterValue,
            page,
            perPage: itemsPerPage,
            sortOpts,
            extraParams: extraFilters,
        })
            .then(this.setRecords)
            .then(() => this.clearRecordsAfterPage(page))
            .finally(() => setLoading ? setLoading(false) : null);
    }

    handleExtraFiltersChange(e) {
        let extraFilters;
        if (e.hasOwnProperty('name') && e.hasOwnProperty('value')) {
            extraFilters = {
                [e.name]: e.value,
            };
        } else if (isPlainObject(e)) {
            extraFilters = e;
        } else {
            extraFilters = {
                [e.target.name]: e.target.value,
            };
        }
        this.setState(prevState => ({
            extraFilters: {
                ...prevState.extraFilters,
                ...extraFilters,
            }
        }), () => this.handleRefreshTable(true));
    }

    render() {
        const {recordPages} = this.state;
        const {
            TableCore,
            ExtraFilters,
            searchBoxPlaceholder,
            extraFilterProps = {},
            restProps,
        } = this.props;
        const {showSearch = true} = restProps;
        const {itemsPerPage, filterValue, extraFilters} = this.state;
        return (
            <div className="thinkscape-table-listing">
                <div className="filters">
                    <div className="common-filters">
                        {
                            !!recordPages.pageCount && (
                                <div className="filter item-per-page">
                                    <span>Show: </span>
                                    <TextField
                                        className="filter-input"
                                        select
                                        value={itemsPerPage}
                                        onChange={this.handleRecordsPerPageSelect}
                                        variant='outlined'
                                    >
                                        <MenuItem value={5}>5</MenuItem>
                                        <MenuItem value={10}>10</MenuItem>
                                        <MenuItem value={20}>20</MenuItem>
                                        <MenuItem value={50}>50</MenuItem>
                                    </TextField>
                                </div>
                            )
                        }
                        {showSearch && <div className="filter search">
                            <TextField
                                className="filter-input"
                                type="text"
                                placeholder={searchBoxPlaceholder || 'Search'}
                                value={filterValue}
                                onChange={this.handleFilterChange}
                                variant='outlined'
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon/>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </div>}
                    </div>
                    {
                        ExtraFilters && (
                            <ExtraFilters
                                ref={ref => this.extraFiltersRef = ref}
                                values={extraFilters}
                                onChange={this.handleExtraFiltersChange}
                                {...extraFilterProps}
                            />
                        )
                    }
                </div>
                <div className="table">
                    <TableCore {...{
                        recordPages,
                        onSortableFieldClick: this.handleSortableFieldClick,
                        ...restProps,
                    }} />
                </div>
                <div className="table-footer">
                    <span className="records-details">
                        {
                            !!recordPages.pageCount && calcTableEntriesInfo(
                                itemsPerPage,
                                recordPages.currentPage,
                                recordPages.totalCount,
                            )
                        }
                    </span>
                    {
                        !!recordPages.pageCount && React.createElement(
                            createUltimatePagination({
                                itemTypeToComponent: {
                                    'PAGE': ({value, isActive, isDisabled, onClick}) => (
                                        <Button
                                            color={isActive ? 'primary' : 'default'}
                                            disabled={isDisabled}
                                            onClick={onClick}
                                        >{value}</Button>
                                    ),
                                    'ELLIPSIS': ({isDisabled, onClick}) => (
                                        <Button disabled={isDisabled} onClick={onClick}><MoreHorizIcon/></Button>
                                    ),
                                    'NEXT_PAGE_LINK': ({isDisabled, onClick}) => (
                                        <Button disabled={isDisabled} onClick={onClick}><ChevronRightIcon/></Button>
                                    ),
                                    'PREVIOUS_PAGE_LINK': ({isDisabled, onClick}) => (
                                        <Button disabled={isDisabled} onClick={onClick}><ChevronLeftIcon/></Button>
                                    ),
                                    'FIRST_PAGE_LINK': ({isDisabled, onClick}) => (
                                        <Button disabled={isDisabled} onClick={onClick}><FirstPageIcon/></Button>
                                    ),
                                    'LAST_PAGE_LINK': ({isDisabled, onClick}) => (
                                        <Button disabled={isDisabled} onClick={onClick}><LastPageIcon/></Button>
                                    ),

                                },
                                WrapperComponent: (props) => <div className="pagination">{props.children}</div>,
                            }),
                            {
                                currentPage: recordPages.currentPage,
                                totalPages: recordPages.pageCount,
                                onChange: this.handlePageSelect,
                            }
                        )
                    }
                </div>
            </div>
        );
    }
}

export default (TableCore, ExtraFilters) => (
    class __ThinkscapeTableWrapper extends React.Component {
        static propTypes = {
            fetchRecords: PropTypes.func.isRequired,
            searchBoxPlaceholder: PropTypes.string,
            TableCore: PropTypes.func,
            extraFilterProps: PropTypes.object,
        };

        render() {
            const {
                fetchRecords,
                searchBoxPlaceholder,
                extraFilterProps,
                ...restProps
            } = this.props;
            return (
                <LoadingContext.Consumer>
                    {value =>
                        <ThinkscapeTableWrapper {...{
                            fetchRecords,
                            setLoading: value.setLoading,
                            searchBoxPlaceholder,
                            restProps,
                            extraFilterProps,
                            TableCore,
                            ExtraFilters,
                        }} />
                    }
                </LoadingContext.Consumer>
            );
        }
    }
);
